/**
 * Tooltip components
 * Simple tooltip components using Tailwind CSS
 */

import React, { createContext, useCallback, useContext, useMemo, useState, useRef } from 'react'
import { cn } from '@/lib/utils'

// Provider Context
interface TooltipProviderContextType {
  delayDuration: number
}

const TooltipProviderContext = createContext<TooltipProviderContextType>({ delayDuration: 200 })

// Tooltip Context
interface TooltipContextType {
  open: boolean
  setOpen: (open: boolean) => void
}

const TooltipContext = createContext<TooltipContextType | null>(null)

// Tooltip Provider
interface TooltipProviderProps {
  delayDuration?: number
  children: React.ReactNode
}

export const TooltipProvider: React.FC<TooltipProviderProps> = ({
  delayDuration = 200,
  children,
}) => {
  const value = useMemo(() => ({ delayDuration }), [delayDuration])

  return (
    <TooltipProviderContext.Provider value={value}>
      {children}
    </TooltipProviderContext.Provider>
  )
}

// Tooltip Root
interface TooltipProps {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
}

export const Tooltip: React.FC<TooltipProps> = ({
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  children,
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen)

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (controlledOpen === undefined) {
        setInternalOpen(newOpen)
      }
      onOpenChange?.(newOpen)
    },
    [controlledOpen, onOpenChange]
  )

  const contextValue = useMemo(
    () => ({ open, setOpen: handleOpenChange }),
    [open, handleOpenChange]
  )

  return (
    <TooltipContext.Provider value={contextValue}>
      <div className="relative inline-block" style={{ position: 'relative', display: 'inline-block' }}>
        {children}
      </div>
    </TooltipContext.Provider>
  )
}

// Tooltip Trigger
interface TooltipTriggerProps extends React.HTMLAttributes<HTMLElement> {
  asChild?: boolean
  children: React.ReactNode
}

export const TooltipTrigger = React.forwardRef<HTMLElement, TooltipTriggerProps>(
  ({ asChild, children, onMouseEnter, onMouseLeave, onFocus, onBlur, ...props }, ref) => {
    const context = useContext(TooltipContext)
    const providerContext = useContext(TooltipProviderContext)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null)

    if (!context) throw new Error('TooltipTrigger must be used within Tooltip')

    const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
      timeoutRef.current = setTimeout(() => {
        context.setOpen(true)
      }, providerContext.delayDuration)
      if (onMouseEnter) {
        ;(onMouseEnter as React.MouseEventHandler<HTMLElement>)(e)
      }
    }

    const handleMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      context.setOpen(false)
      if (onMouseLeave) {
        ;(onMouseLeave as React.MouseEventHandler<HTMLElement>)(e)
      }
    }

    const handleFocus = (e: React.FocusEvent<HTMLElement>) => {
      context.setOpen(true)
      if (onFocus) {
        ;(onFocus as React.FocusEventHandler<HTMLElement>)(e)
      }
    }

    const handleBlur = (e: React.FocusEvent<HTMLElement>) => {
      context.setOpen(false)
      if (onBlur) {
        ;(onBlur as React.FocusEventHandler<HTMLElement>)(e)
      }
    }

    // If asChild is true and children is a single React element, clone it with handlers
    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(
        children as React.ReactElement<{
          onMouseEnter?: React.MouseEventHandler
          onMouseLeave?: React.MouseEventHandler
          onFocus?: React.FocusEventHandler
          onBlur?: React.FocusEventHandler
          ref?: React.Ref<HTMLElement>
        }>,
        {
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave,
          onFocus: handleFocus,
          onBlur: handleBlur,
          ref,
        }
      )
    }

    return (
      <span
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...props}
      >
        {children}
      </span>
    )
  }
)
TooltipTrigger.displayName = 'TooltipTrigger'

// Tooltip Content
interface TooltipContentProps extends React.HTMLAttributes<HTMLDivElement> {
  side?: 'top' | 'right' | 'bottom' | 'left'
  sideOffset?: number
  children: React.ReactNode
}

export const TooltipContent = React.forwardRef<HTMLDivElement, TooltipContentProps>(
  ({ className, side = 'top', sideOffset = 4, children, ...props }, ref) => {
    const context = useContext(TooltipContext)

    if (!context) throw new Error('TooltipContent must be used within Tooltip')

    if (!context.open) return null

    // Position classes based on side (kept for projects where Tailwind is configured)
    const positionClasses = {
      top: 'bottom-full left-0 mb-2',
      bottom: 'top-full left-0 mt-2',
      left: 'right-full top-1/2 -translate-y-1/2 mr-2',
      right: 'left-full top-1/2 -translate-y-1/2 ml-2',
    }

    // Inline fallback styles: this app doesn't run a Tailwind build, so the
    // classes above have no effect here. Without real positioning/background,
    // the content rendered in normal flow and visually ran into whatever sat
    // below the trigger. These inline styles make the tooltip actually float.
    const positionStyles: Record<string, React.CSSProperties> = {
      top: { bottom: '100%', left: 0, marginBottom: sideOffset },
      bottom: { top: '100%', left: 0, marginTop: sideOffset },
      left: { right: '100%', top: '50%', transform: 'translateY(-50%)', marginRight: sideOffset },
      right: { left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: sideOffset },
    }

    return (
      <div
        ref={ref}
        className={cn(
          'absolute z-50 overflow-hidden rounded-md bg-gray-900 px-3 py-1.5 text-xs text-white shadow-md animate-in fade-in-0 zoom-in-95',
          'dark:bg-gray-700',
          positionClasses[side],
          className
        )}
        style={{
          position: 'absolute',
          zIndex: 50,
          maxWidth: 256,
          width: 'max-content',
          borderRadius: 6,
          backgroundColor: '#111827',
          color: '#fff',
          padding: '6px 12px',
          fontSize: 12,
          lineHeight: 1.4,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
          ...positionStyles[side],
        }}
        {...props}
      >
        {children}
      </div>
    )
  }
)
TooltipContent.displayName = 'TooltipContent'
